import { ReactElement, useState } from 'react';
import { Button, Divider, Form, Input, Select } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { I18n } from '@aws-amplify/core';

import { Evaluator } from '@/types';
import { useCreateEvaluator } from '@/hooks/v1';
import { CreateEvaluatorRequest } from '../types/request';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useApplicationContext } from '@/context/Application';

const { useFormInstance } = Form;

type EvaluatorSelectProps = {
	size?: SizeType;
	loading: boolean;
	disabled: boolean;
	companyId?: string;
	organizationId?: string;
	fieldName: NamePath;
	evaluatorId?: string;
	allowCreation?: boolean;
	handleClearEvaluators?: () => void;
	evaluators: Evaluator[] | undefined;
	handleSelectEvaluator?: (id: string) => void;
};

type OptionType = { label: string; value: string } | undefined;

export function EvaluatorSelect({
	loading,
	disabled,
	fieldName,
	companyId,
	organizationId,
	evaluators,
	evaluatorId,
	size = 'middle',
	allowCreation = true,
	handleSelectEvaluator,
	handleClearEvaluators
}: Readonly<EvaluatorSelectProps>) {
	const [evaluatorName, setEvaluatorName] = useState('');

	const { validateFields } = useFormInstance();
	const { company, organization } = useApplicationContext();

	const { mutateAsync: createEvaluator, isLoading } = useCreateEvaluator();

	async function onCreateEvaluator() {
		const body: CreateEvaluatorRequest = {
			company_id: companyId ?? company.id,
			organization_id: organizationId ?? organization.id,
			name: evaluatorName
		};

		const { data } = await createEvaluator(body);
		const evaluatorId = data?.id;
		if (evaluatorId) {
			handleChange(evaluatorId);
		}
		setEvaluatorName('');
	}

	async function handleChange(id: string) {
		if (handleSelectEvaluator) {
			handleSelectEvaluator(id);
			await validateFields([fieldName]);
		}
	}

	function handleClear() {
		if (handleClearEvaluators) {
			handleClearEvaluators();
		}
	}

	function handleEvaluatorNameChange(name: string) {
		setEvaluatorName(name);
	}

	function filterOption(option: OptionType, input: string): boolean {
		return (option?.label.toLowerCase() ?? '').includes(input.toLowerCase());
	}

	function showDropdownRender(menu: ReactElement): JSX.Element {
		if (!allowCreation) {
			return <div>{menu}</div>;
		}
		return (
			<div>
				{menu}
				<Divider style={{ margin: '4px 0' }} />
				<div
					style={{
						display: 'flex',
						flexWrap: 'nowrap',
						padding: 8
					}}
				>
					<Input
						style={{ flex: 'auto', borderRadius: '7px' }}
						value={evaluatorName}
						onChange={(e) => handleEvaluatorNameChange(e.target.value)}
					/>
					<Button
						onClick={onCreateEvaluator}
						type="link"
						loading={isLoading}
						disabled={!evaluatorName || evaluatorName.length < 3}
					>
						{I18n.get('Add item')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<Select
			showSearch
			allowClear
			size={size}
			loading={loading}
			disabled={disabled}
			value={evaluatorId}
			onClear={handleClear}
			placeholder={I18n.get('Evaluator')}
			onChange={(value: string) => handleChange(value)}
			dropdownRender={(menu) => showDropdownRender(menu)}
			filterOption={(input, option) => filterOption(option, input)}
			options={evaluators?.map(({ id, name }) => ({ label: name, value: id }))}
		/>
	);
}
